import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useEncounterCreationGate } from "../../hooks/use-encounter-creation-gate";
import { PatientVitalCustom } from "../../pages/app/provider/patient/patient-list/patient-chart-new/vitals/vital-constant";
import { PatientVitalControllerApi } from "../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../../utils/utils";

interface Props {
  patientUuid?: string;
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  sortBy?: string;
  sortDirection?: string;
  active?: boolean;
  archive?: boolean;
  appointmentUuid?: string;
  searchString?: string;
  xTENANTID?: string | undefined;
  enabled?: boolean;
}

export type VitalData = {
  details: Array<PatientVitalCustom>;
  recordedDate: string;
  vitalCount: string;
  uuid: string;
};

/**
 *
 * @author Rajkumar Rathod
 */
export const usePatientVitalsList = ({
  patientUuid,
  pagination,
  gridSorting,
  sortDirection,
  active,
  archive,
  appointmentUuid,
  searchString,
  xTENANTID,
  enabled = true,
}: Props): useBaseContent<VitalData> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: searchString,
    prevPage,
  });
  const pageSize = getPageSize(searchString, pagination);

  const sortDirect = gridSorting?.[0]?.sort ?? undefined;
  const sortBy = sortDirect ? "firstName" : undefined;
  const skip = useEncounterCreationGate();
  return useQuery({
    queryKey: [
      "patient-vital-list",
      patientUuid,
      pageIndex,
      pageSize,
      sortBy,
      sortDirection,
      active,
      archive,
      appointmentUuid,
      searchString,
      xTENANTID,
    ],
    enabled: enabled && !skip,
    refetchOnWindowFocus: false,

    placeholderData: keepPreviousData,
    queryFn: async () =>
      await new PatientVitalControllerApi(getConfigObject())
        .getPatientVitals(
          pageIndex, //page
          pageSize, //size
          sortBy,
          sortDirection, // sortDirection
          active,
          archive,
          patientUuid,
          appointmentUuid,
          searchString,
          xTENANTID // xTENANTID
        )
        .then((res) => res?.data),
  });
};
